/**
 * Reconciliation utilities for matching time entries across
 * Webapp (Cosmos), AutoTask, and TimeTech systems.
 */

import { CalendarTimeEntry, } from '../../services/types';
import { ReconcileAutoTaskEntry, ReconcileTimeTechEntry } from '../../services/api';

export type MatchStatus = 'matched' | 'partial' | 'only_webapp' | 'only_autotask' | 'only_timetech';

export interface ReconciledEntry {
  matchStatus: MatchStatus;
  dateWorked: string; // YYYY-MM-DD
  webapp?: CalendarTimeEntry;
  autotask?: ReconcileAutoTaskEntry;
  timetech?: ReconcileTimeTechEntry;
  hoursMismatch: boolean;
}

export interface ReconcileDayGroup {
  date: Date;
  dateStr: string;
  entries: ReconciledEntry[];
  totals: {
    webapp: number;
    autotask: number;
    timetech: number;
  };
}

export interface ReconcileSummary {
  totalMatched: number;
  onlyWebapp: number;
  onlyAutotask: number;
  onlyTimetech: number;
  hoursMismatches: number;
  totalHours: {
    webapp: number;
    autotask: number;
    timetech: number;
  };
}

/**
 * Normalize TimeTech date "M/d/yyyy" → "YYYY-MM-DD"
 */
export function normalizeTimeTechDate(dateStr: string): string {
  const parts = dateStr.split('/');
  if (parts.length !== 3) return dateStr;
  const month = parts[0].padStart(2, '0');
  const day = parts[1].padStart(2, '0');
  const year = parts[2];
  return `${year}-${month}-${day}`;
}

/**
 * Calculate total hours from a TimeTech entry
 */
export function timeTechHours(entry: ReconcileTimeTechEntry): number {
  return entry.Hours + entry.Minutes / 60;
}

/**
 * Simple text similarity (word overlap ratio)
 */
function textSimilarity(a: string, b: string): number {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const wordsA = new Set(a.toLowerCase().replace(/[[\]]/g, '').split(/\s+/).filter(Boolean));
  const wordsB = new Set(b.toLowerCase().replace(/[[\]]/g, '').split(/\s+/).filter(Boolean));
  if (wordsA.size === 0 && wordsB.size === 0) return 1;
  let overlap = 0;
  for (const w of wordsA) {
    if (wordsB.has(w)) overlap++;
  }
  return overlap / Math.max(wordsA.size, wordsB.size);
}

/**
 * Reconcile entries from all three systems
 */
export function reconcileEntries(
  webapp: CalendarTimeEntry[],
  autotask: ReconcileAutoTaskEntry[],
  timetech: ReconcileTimeTechEntry[]
): ReconciledEntry[] {
  const results: ReconciledEntry[] = [];

  // Track which entries have been matched
  const matchedWebapp = new Set<string>();
  const matchedAutotask = new Set<number>();
  const matchedTimetech = new Set<number>();

  // Phase 1: ID-based matching (webapp ↔ autotask via autoTaskId)
  for (const we of webapp) {
    if (we.autoTaskId) {
      const at = autotask.find(a => a.id === we.autoTaskId);
      if (at) {
        matchedWebapp.add(we.id);
        matchedAutotask.add(at.id);

        // Look for matching TimeTech entry via timetechTimeEntryId
        let tt: ReconcileTimeTechEntry | undefined;
        if (we.timetechTimeEntryId) {
          tt = timetech.find(t => t.TimeEntryId === we.timetechTimeEntryId);
          if (tt) matchedTimetech.add(tt.TimeEntryId);
        }

        const ttHours = tt ? timeTechHours(tt) : undefined;
        const hoursMismatch = tt
          ? Math.abs(we.hoursWorked - at.hoursWorked) > 0.01 || Math.abs(we.hoursWorked - ttHours!) > 0.01
          : Math.abs(we.hoursWorked - at.hoursWorked) > 0.01;

        results.push({
          matchStatus: tt ? 'matched' : 'partial',
          dateWorked: we.dateWorked,
          webapp: we,
          autotask: at,
          timetech: tt,
          hoursMismatch,
        });
      }
    }
  }

  // Phase 1b: ID-based matching (webapp ↔ timetech via timetechTimeEntryId, not yet matched)
  for (const we of webapp) {
    if (matchedWebapp.has(we.id)) continue;
    if (we.timetechTimeEntryId) {
      const tt = timetech.find(t => t.TimeEntryId === we.timetechTimeEntryId && !matchedTimetech.has(t.TimeEntryId));
      if (tt) {
        matchedWebapp.add(we.id);
        matchedTimetech.add(tt.TimeEntryId);

        results.push({
          matchStatus: 'partial',
          dateWorked: we.dateWorked,
          webapp: we,
          timetech: tt,
          hoursMismatch: Math.abs(we.hoursWorked - timeTechHours(tt)) > 0.01,
        });
      }
    }
  }

  // Phase 2: Fuzzy matching for remaining unmatched entries
  const unmatchedWebapp = webapp.filter(w => !matchedWebapp.has(w.id));
  const unmatchedAutotask = autotask.filter(a => !matchedAutotask.has(a.id));
  const unmatchedTimetech = timetech.filter(t => !matchedTimetech.has(t.TimeEntryId));

  // Try to match remaining webapp entries to autotask by date+hours+notes
  for (const we of unmatchedWebapp) {
    let bestAt: ReconcileAutoTaskEntry | undefined;
    let bestAtScore = 0;

    for (const at of unmatchedAutotask) {
      if (matchedAutotask.has(at.id)) continue;
      // Must match on date
      const atDate = at.dateWorked.split('T')[0];
      if (atDate !== we.dateWorked) continue;
      // Hours close enough
      if (Math.abs(we.hoursWorked - at.hoursWorked) > 0.01) continue;
      // Note similarity
      const sim = textSimilarity(we.summaryNotes || '', at.summaryNotes || '');
      if (sim > bestAtScore) {
        bestAtScore = sim;
        bestAt = at;
      }
    }

    if (bestAt && bestAtScore > 0.3) {
      matchedWebapp.add(we.id);
      matchedAutotask.add(bestAt.id);

      // Also try to match TimeTech
      let bestTt: ReconcileTimeTechEntry | undefined;
      let bestTtScore = 0;
      for (const tt of unmatchedTimetech) {
        if (matchedTimetech.has(tt.TimeEntryId)) continue;
        if (normalizeTimeTechDate(tt.DateEntered) !== we.dateWorked) continue;
        if (Math.abs(we.hoursWorked - timeTechHours(tt)) > 0.01) continue;
        const sim = textSimilarity(we.summaryNotes || '', tt.Description || '');
        if (sim > bestTtScore) {
          bestTtScore = sim;
          bestTt = tt;
        }
      }

      if (bestTt && bestTtScore > 0.2) {
        matchedTimetech.add(bestTt.TimeEntryId);
      }

      results.push({
        matchStatus: bestTt ? 'matched' : 'partial',
        dateWorked: we.dateWorked,
        webapp: we,
        autotask: bestAt,
        timetech: bestTt,
        hoursMismatch: false,
      });
    }
  }

  // Phase 3: Remaining unmatched entries
  for (const we of webapp) {
    if (matchedWebapp.has(we.id)) continue;
    results.push({
      matchStatus: 'only_webapp',
      dateWorked: we.dateWorked,
      webapp: we,
      hoursMismatch: false,
    });
  }

  for (const at of autotask) {
    if (matchedAutotask.has(at.id)) continue;
    const dateWorked = at.dateWorked.split('T')[0];
    results.push({
      matchStatus: 'only_autotask',
      dateWorked,
      autotask: at,
      hoursMismatch: false,
    });
  }

  for (const tt of timetech) {
    if (matchedTimetech.has(tt.TimeEntryId)) continue;
    results.push({
      matchStatus: 'only_timetech',
      dateWorked: normalizeTimeTechDate(tt.DateEntered),
      timetech: tt,
      hoursMismatch: false,
    });
  }

  return results;
}

/**
 * Group reconciled entries by date
 */
export function groupByDate(entries: ReconciledEntry[]): ReconcileDayGroup[] {
  const groups = new Map<string, ReconciledEntry[]>();

  for (const entry of entries) {
    const key = entry.dateWorked;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(entry);
  }

  const result: ReconcileDayGroup[] = [];
  for (const [dateStr, dayEntries] of groups) {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const totals = { webapp: 0, autotask: 0, timetech: 0 };
    for (const e of dayEntries) {
      if (e.webapp) totals.webapp += e.webapp.hoursWorked;
      if (e.autotask) totals.autotask += e.autotask.hoursWorked;
      if (e.timetech) totals.timetech += timeTechHours(e.timetech);
    }

    result.push({ date, dateStr, entries: dayEntries, totals });
  }

  return result.sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * Calculate summary statistics
 */
export function calculateSummary(entries: ReconciledEntry[]): ReconcileSummary {
  const summary: ReconcileSummary = {
    totalMatched: 0,
    onlyWebapp: 0,
    onlyAutotask: 0,
    onlyTimetech: 0,
    hoursMismatches: 0,
    totalHours: { webapp: 0, autotask: 0, timetech: 0 },
  };

  for (const e of entries) {
    switch (e.matchStatus) {
      case 'matched': summary.totalMatched++; break;
      case 'partial': summary.totalMatched++; break;
      case 'only_webapp': summary.onlyWebapp++; break;
      case 'only_autotask': summary.onlyAutotask++; break;
      case 'only_timetech': summary.onlyTimetech++; break;
    }
    if (e.hoursMismatch) summary.hoursMismatches++;
    if (e.webapp) summary.totalHours.webapp += e.webapp.hoursWorked;
    if (e.autotask) summary.totalHours.autotask += e.autotask.hoursWorked;
    if (e.timetech) summary.totalHours.timetech += timeTechHours(e.timetech);
  }

  return summary;
}
